// Angular
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatTreeModule } from '@angular/material/tree';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatStepperModule } from '@angular/material/stepper';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { MatPaginatorModule } from '@angular/material/paginator';

// Ionic
import { NgModule, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { Drivers } from '@ionic/storage';
import { IonicStorageModule } from '@ionic/storage-angular';

// pipes
import { CustomDatePipe } from './pipes/custom-date.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { ParseDateTimePipe } from './pipes/parse-date.pipe';
import { FilterSelectItemsPipe } from './pipes/filter-select-items.pipe';
import { CustomTimePipe } from './pipes/custom-time';
import { FrequencyFormatPipe } from './pipes/plans-frequency-format.pipe';
import { RemoveSpacesPipe } from './pipes/remove-spaces.pipe';
import { TelephonePipe } from './pipes/telephone.pipe';
import { TypePlanFormatPipe } from './pipes/plan-type-format.pipe';
import { FirstNamePipe } from './pipes/first-name.pipe';
import { FormatStatePipe } from './pipes/format-state.pipe';
import { TidyAbbreviateLastName } from 'src/shared/pipes/tidy-abbr-lastname.pipe'
import { TidyCurrencyPipe } from 'src/shared/pipes/tidy-currency.pipe';
import { TidyNumberToWeekDayPipe } from './pipes/tidy-number-to-weekday.pipe'
import { ShortNamePipe } from './pipes/short-name.pipe';
import { InitialsPipe } from './pipes/initials.pipe';
import { TimeIndicatorPipe } from './pipes/time-indicator.pipe';
import { CapitalizePipe } from './pipes/capitalize.pipe';
import { TranslationPipe } from './pipes/translation.pipe';
import { FormatJobDatePipe } from './pipes/format-job-date.pipe';
import { FormatCentsToDollarsPipe } from './pipes/format-cents-to-dollars.pipe';
import { FormatDueDatePipe } from './pipes/format-due-date.pipe';
import { ServiceDurationPipe } from './pipes/service-duration.pipe';
import { IssueHeaderIconPipe } from './pipes/issue-header-icon.pipe';
import { WeatherIconPipe } from './pipes/weather-icon.pipe';
import { ServiceIconPipe } from './pipes/service-icon.pipe';
import { DisputeIconPipe } from './pipes/dispute-icon.pipe';
import { ThemeIconPipe } from './pipes/theme-icon.pipe';
import { MessageTimePipe } from './pipes/message-time.pipe';
import { FormatLocaleNumberPipe } from './pipes/format-locale-number.pipe';
import { LinkifyPipe } from './pipes/linkify.pipe';
import { EnrichLinksPipe } from './pipes/enrich-links.pipe';
import { IsCounterpartyMessagePipe } from './pipes/is-counterparty-message.pipe';
import { IsTextOnlyGuestChatRoomPipe } from './pipes/is-text-only-guest-chat-room.pipe';
import { ExtractPaymentLinkUuidPipe } from './pipes/extract-payment-link-uuid.pipe';
import { InlinePaymentFormComponent } from './components/inline-payment-form/inline-payment-form.component';
import { EveryPipe } from './pipes/every.pipe';
import { IsImageUrlPipe } from './pipes/is-image-url.pipe';
import { FilenameFromUrlPipe } from './pipes/filename-from-url.pipe';
import { IsPdfUrlPipe } from './pipes/is-pdf-url.pipe';
import { IsCsvUrlPipe } from './pipes/is-csv-url.pipe';
import { SafeResourceUrlPipe } from './pipes/safe-resource-url.pipe';
import { EscapeHtmlPipe } from './pipes/escape-html.pipe';
import { CsvPreviewComponent } from './components/csv-preview/csv-preview.component';
import { AttachmentCarouselComponent } from './components/attachment-carousel/attachment-carousel.component';

// components
import { TidyDateScrollerComponent } from './components/tidy-date-scroller/tidy-date-scroller.component';
import { TidyTimeScrollerComponent } from './components/tidy-time-scroller/tidy-time-scroller.component';
import { TasksFilterComponent } from 'src/pages/tasks/tasks-filter/tasks-filter';
import { ScheduleWeeklyFilterComponent } from 'src/pages/schedule/schedule-weekly-filter/schedule-weekly-filter';
import { ScheduleWeeklyViewComponent } from 'src/pages/schedule/schedule-weekly-view/schedule-weekly-view';
import { ScheduleDayViewComponent } from 'src/pages/schedule/schedule-day-view/schedule-day-view';
import { ScheduleListViewComponent } from 'src/pages/schedule/schedule-list-view/schedule-list-view';
import { ChipTextArea } from './components/chip-text-area/chip-text-area';
import { AccountTypeSelectionComponent } from './components/account-type-selector/account-type-selection';
import { AutocompleteAddressComponent } from 'src/shared/components/autocomplete-address/autocomplete-address';
import { CleaningTypePlan } from './components/cleaning-type-plan/cleaning-type-plan';
import { NavBarDesktop } from './components/nav-bar-desktop/nav-bar-desktop';
import { PastHomekeeper } from './components/past-homekeeper/past-homekeeper';
import { TodosMenu } from './components/todos-menu/todos-menu';
import { TeamTreeComponent } from './components/tree-component/tree.component';
import { VideoScanner } from 'src/pages/more/edit-address/property-mapping/scanner-component/scanner-component'
import { FeedbackComponent } from 'src/pages/feedback/feedback.component'
import { SubscriptionModalComponent } from './components/subscription-modal/subscription-modal'

import { TidyCardDirective } from './theme-components/tidy-card/tidy-card.directive';
import { TidyCardHeader } from './theme-components/tidy-card/tidy-card-header.component';
import { TidySelectSmall } from './theme-components/tidy-select-small/tidy-select-small';
import { TidyCardList } from './theme-components/tidy-card/tidy-card-list.component';
import { TidyCardTitleDirective } from './theme-components/tidy-card/tidy-card-title.directive';
import { TidyLinkDirective } from './theme-components/tidy-link/tidy-link.directive';
import { TidyCardItem } from './theme-components/tidy-card/tidy-card-item.component';
import { TidyLabelDirective } from './theme-components/tidy-label/tidy-label.directive';

import { TidyCardInput } from './theme-components/tidy-card-input/tidy-card-input.component';
import { TidyCardSelect } from './theme-components/tidy-card-select/tidy-card-select.component';
import { TidyCardIndicator } from './theme-components/tidy-card-indicator/tidy-card-indicator.component';

import { TidyMenuAddressSelectorComponent } from './theme-components/tidy-menu/tidy-menu-address-selector/tidy-menu-address-selector.component';
import { TidyMenuComponent } from './theme-components/tidy-menu/tidy-menu';
import { TidyMenuHeader } from './theme-components/tidy-menu/tidy-menu-header/tidy-menu-header.component';
import { TidyMenuTopButtonsComponent } from './theme-components/tidy-menu/tidy-menu-top-buttons/tidy-menu-top-buttons.component';
import { TidyMenuBottomButtonsComponent } from './theme-components/tidy-menu/tidy-menu-bottom-buttons/tidy-menu-bottom-buttons.component';
import { TidyBigIconComponent } from './theme-components/tidy-big-icon/tidy-big-icon.component';
import { TidyMissingField } from './theme-components/tidy-menu/tidy-missing-field/tidy-missing-field';

import { ToDosOnboardingComponent } from 'src/shared/components/to-dos-onboarding/to-dos-onboarding';

import { ParkingReimbursementComponent } from 'src/shared/components/parking-reimbursement/parking-reimbursement.component';
import { PlanFrequencyMessage } from './components/plan-frequency-message/plan-frequency-message';
import { PhotoNote } from './components/photo-note/photo-note';
import { PhotoCaption } from 'src/shared/components/photo-caption/photo-caption.component';

import { TidyActiveButtonComponent } from './components/tidy-active-button/tidy-active-button';

import { TranslateOfficePipe } from './pipes/translate-office-pipe';
import { ReplaceEnterToBlurDirective } from './directives/replace-enter-to-blur';
import { CustomTextareaDirective } from './directives/custom-textarea';
import { ChangeSquareClassDirective } from './directives/change-square-class';
import { GoToNextDirective } from './directives/go-to-next';
import { IonicCdkScrollableDirective } from './directives/ionic-cdk-scrollable.directive';
import { GenomeItemsComponent } from 'src/shared/components/genome-items/genome-items.component';

import { FrequencyRequestTimesComponent } from './components/craf/frequency-request-times/frequency-request-times.component';
import { ListRequestTimesComponent } from './components/craf/list-request-times/list-request-times.component';
import { RequestTimesSectionComponent } from './components/craf/request-times-section/request-times-section.component';
import { MaintenancesCardsComponent } from 'src/shared/components/maintenances-cards/maintenances-cards.component'
import { MessageComponent } from 'src/shared/components/message/message.component'
import { ImagePreviewComponent } from 'src/shared/components/image-preview/image-preview.component'
import { JobFormPopupComponent } from 'src/shared/components/job-form-popup/job-form-popup.component'
import { DesktopHeaderComponent } from 'src/shared/components/desktop-header/desktop-header';
import { NotificationHistoryComponent } from 'src/shared/components/notification-history/notification-history.component'

import { DesktopNavComponent } from 'src/shared/components/desktop-nav/desktop-nav.component';
import { TidyNavbar } from './components/tidy-navbar/tidy-navbar';
import { TopNavComponent } from 'src/shared/components/top-nav/top-nav.component';
import { DashboardChatComponent } from 'src/shared/components/dashboard-chat/dashboard-chat.component';
import { DashboardChatSidebarComponent } from 'src/shared/components/dashboard-chat-sidebar/dashboard-chat-sidebar.component';
import { ExpandableChatComponent } from 'src/shared/components/expandable-chat/expandable-chat.component';

import { FilterNestedFormComponent } from 'src/pages/workflows/filter-nested-form/filter-nested-form';
import { PdfViewerComponent } from 'src/pages/more/past-job-pdf/pdf-viewer.component';

// Services
import { GetCleaningsService } from './services/craf/get-cleanings.service';

// pages
import { BottomNavComponent } from 'src/shared/components/bottom-nav/bottom-nav-component';
import { SuccessPage } from 'src/shared/pages/success/success';

// providers
import { CustomNavParams } from './providers/custom-nav-params/custom-nav-params';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RoutingState } from 'src/shared/providers/providers/routing-state';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { CameraPreviewComponent } from './components/camera-preview/camera-preview.component';
import { DynamicComponentProvider } from './providers/dynamic-component/dynamic-component';
import { MapPopup } from 'src/pages/dashboard/map-popup/map-popup';
import { RemoveEmployeeComponent } from 'src/pages/employer/edit-employee/confirm-remove-employee/confirm-remove-employee';
import { AddActionModal } from 'src/pages/workflows/add-action-modal/add-action-modal';
import { DropDownMenu } from 'src/pages/workflows/drop-down-menu/drop-down-menu';
import { OutsideClickDirective } from './directives/outside-click';
import { DropDownMenuItem } from 'src/pages/workflows/drop-down-menu/drop-down-menu-item/drop-down-menu-item';
import { SelectOptionsPipe } from './pipes/select-options.pipe';
import { TidyDateFormatPipe } from './pipes/tidy-date.pipe';
import { TruncatePipe } from './pipes/truncate.pipe';
import { UrlFileNamePipe } from './pipes/url-file-name.pipe';
import { removeUnderscorePipe } from './pipes/remove-underscore.pipe';
import { TimeAgoPipe } from './pipes/time-ago.pipe';
import { IsSelectedPipe } from './pipes/is-selected.pipe';
import { HideAssignedProDidNotContactPipe } from './pipes/hide-assigned-pro-did-not-contact.pipe';
import { CoverageTypeLabelPipe } from './pipes/coverage-type-label.pipe';
import { RecurringTasksFilter } from 'src/pages/tasks/recurring-tasks/recurring-tasks-filter/recurring-tasks-filter';
import { DialogContentComponent } from './components/dynamic-dialog-container/dynamic-dialog-container';
import { ConciergeFilterComponent } from 'src/pages/concierge/concierge-filter/concierge-filter';
import { JobMediasProvider } from 'src/providers/job-medias/job-medias.provider';
import { ScheduleMonthlyViewComponent } from 'src/pages/schedule/schedule-monthly-view/schedule-monthly-view';
import { ReservationCardComponent } from 'src/pages/schedule/components/reservation-card/reservation-card.component';
import { ReservationPopoverComponent } from 'src/pages/schedule/components/reservation-popover/reservation-popover.component';
import { ReservationBarComponent } from 'src/pages/schedule/components/reservation-bar/reservation-bar.component';
import { OwnerBlockCardComponent } from 'src/pages/schedule/components/owner-block-card/owner-block-card.component';
import { ScheduleWeeklySkeletonComponent } from 'src/pages/schedule/schedule-weekly-view/schedule-weekly-skeleton.component';
import { BookingRequestsBannerComponent } from 'src/shared/components/booking-requests-banner/booking-requests-banner.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { HeaderComponent } from './components/header/header';
import { PaymentMethodCardComponent } from 'src/pages/more/payment-methods/payment-method-card/payment-method-card';

//Onboarding Goals Modal:
import { OnboardingComponent } from './components/onboarding/onboarding';
import { GoalTasksCardComponent } from './components/onboarding/goal-tasks-card/goal-tasks-card';
import { AdvancedSetupComponent } from './components/onboarding/advanced-setup/advanced-setup';
import { VideoTutorialComponent } from './components/onboarding/video-tutorial/video-tutorial';

//Onboarding Page Modal:
import { OnboardingModalComponent } from './components/onboarding/onboarding-modal.component';

//Onboarding Modal Pages:
import { RestockingOnboardingComponent } from './components/onboarding/restocking/restocking-onboarding.component';
import { RestockingInputsOnboardingComponent } from './components/onboarding/restocking-onboarding/restocking-onboarding.component';
import { PropertyPhotosOnboardingComponent } from './components/onboarding/property-photos-onboarding/property-photos-onboarding.component';
import { PropertyOnboardingComponent } from './components/onboarding/property/property-onboarding.component';
import { DirectIntegrationOnboardingComponent } from './components/onboarding/direct-integration/direct-integration-onboarding.component';
import { AddDirectIntegrationOnboardingComponent } from './components/onboarding/add-direct-integration/add-direct-integration-onboarding.component';
import { ImportCSVOnboardingComponent } from './components/onboarding/import-csv/import-csv-onboarding.component';
import { AddProsOnboardingComponent } from './components/onboarding/add-pros/add-pros.component';
import { ListingsSetupComponent } from './components/listings-setup/listings-setup.component';
import { ConnectBankOnboardingComponent } from './components/onboarding/connect-bank-onboarding/connect-bank-onboarding.component';
import { SmartlockTestCodeComponent } from './components/smartlock-test-code/smartlock-test-code.component';
import { SmartLockChooserComponent } from './components/smart-lock-chooser/smart-lock-chooser.component';
import { ManageLockAssignmentsComponent } from './components/manage-lock-assignments/manage-lock-assignments.component';
import { LegalComplianceOnboardingComponent } from './components/onboarding/legal-compliance-onboarding/legal-compliance-onboarding.component';
import { GuestMessagingOnboardingComponent } from './components/onboarding/guest-messaging-onboarding/guest-messaging-onboarding.component';
import { DirectBookingOnboardingComponent } from './components/onboarding/direct-booking-onboarding/direct-booking-onboarding.component';
import { ListingOptimizationOnboardingComponent } from './components/onboarding/listing-optimization-onboarding/listing-optimization-onboarding.component';
import { ListingOptimizerReportComponent } from './components/listing-optimizer-report/listing-optimizer-report.component';
import { PricingOptimizationOnboardingComponent } from './components/onboarding/pricing-optimization-onboarding/pricing-optimization-onboarding.component';
import { ChecklistsOnboardingComponent } from './components/onboarding/checklists-onboarding/checklists-onboarding.component';
import { GuidebookOnboardingComponent } from './components/onboarding/guidebook-onboarding/guidebook-onboarding.component';
import { SmartNumberOnboardingComponent } from './components/onboarding/smart-number-onboarding/smart-number-onboarding.component';
import { NotifyProsOnboardingComponent } from './components/onboarding/notify-pros-onboarding/notify-pros-onboarding.component';
import { OnboardingCallComponent } from './components/onboarding/onboarding-call/onboarding-call.component';
import { UploadProsOnboardingComponent } from './components/onboarding/upload-pros/upload-pros.component';
import { SelectIntegrationOnboarding } from './components/onboarding/select-integration-onboarding/select-integration-onboarding';
import { EnableAutomaticBookingOnboarding } from './components/onboarding/enable-automatic-booking-onboarding/enable-automatic-booking-onboarding';
import { CreateConciergeActionOnboarding } from './components/onboarding/create-concierge-action-onboarding/create-concierge-action-onboarding';
import { SelectAccessNotesOptionOnboarding } from './components/onboarding/select-access-notes-option-onboarding/select-access-notes-option-onboarding';
import { InputAccessNotesOnboarding } from './components/onboarding/input-access-notes-onboarding/input-access-notes-onboarding';
import { SelectFindNewProsOptionOnboarding } from './components/onboarding/select-find-new-pros-option-onboarding/select-find-new-pros-option-onboarding';
import { SelectRestockingOptionOnboardingComponent } from './components/select-restocking-option/select-restocking-option.component';
import { SelectMaintenanceOptionOnboarding } from './components/onboarding/select-maintenance-option-onboarding/select-maintenance-option-onboarding';
import { AddMaintenanceItemsOnboarding } from './components/onboarding/add-maintenance-items-onboarding/add-maintenance-items-onboarding';
import { EnableGuestSmartNumberOnboarding } from './components/onboarding/enable-guest-smart-number-onboarding/enable-guest-smart-number-onboarding';
import { AutomateWorkflowsOnboarding } from './components/onboarding/automate-workflows-onboarding/automate-workflows-onboarding';
import { NotifyProsOfChangeOnboarding } from './components/onboarding/notify-pros-of-change-onboarding/notify-pros-of-change-onboarding';
import { EnableConciergeMessagingOnboarding } from './components/onboarding/enable-concierge-messaging-onboarding/enable-concierge-messaging-onboarding';
import { CreateConciergeRulesOnboarding } from './components/onboarding/create-concierge-rules-onboarding/create-concierge-rules-onboarding';
import { ScheduleOnboardingComponent } from './components/onboarding/schedule/schedule.component';
import { TasksOnboardingComponent } from './components/onboarding/tasks/tasks.component';
import { ToDosModalOnboardingComponent } from './components/onboarding/to-dos/to-dos.component';
import { BillsOnboardingComponent } from './components/onboarding/bills/bills.component';
import { AddIntegrationFormComponent } from './components/add-integration-form/add-integration-form.component';
import { AutomaticBookingFormComponent } from './components/automatic-booking-form/automatic-booking-form.component';
import { IntegrateUpsellProviderComponent } from './components/integrate-upsell-provider/integrate-upsell-provider.component';
import { AddInspectionsOnboardingComponent } from './components/onboarding/add-inspections-onboarding/add-inspections-onboarding';
import { ServiceRenameOnboardingComponent } from './components/onboarding/service-rename-onboarding/service-rename-onboarding';
import { PopularListModalOnboardingComponent } from './components/onboarding/to-dos/popular-list/popular-list.component';
import { AccordionComponent } from './components/accordion/accordion.component';
import { MessageCardComponent } from 'src/pages/messages/message-card/message-card';
import { TidyMarkdownComponent } from './components/tidy-markdown/tidy-markdown.component';
import { RestockingIntegrationSelectorComponent } from './components/restocking-integration-selector/restocking-integration-selector.component';
import { RestockingBrandWaterfallComponent } from './components/restocking-brand-waterfall/restocking-brand-waterfall.component';
import { DynamicBookingFormComponent } from './components/dynamic-booking-form/dynamic-booking-form.component';
import { TaskDropdownComponent } from './components/task-dropdown/task-dropdown';
import { StepperComponent } from './components/stepper/stepper.component';
import { ScrollWrapperComponent } from './components/scroll-wrapper/scroll-wrapper.component';
import { TagComponent } from './components/tag/tag.component';
import { TableComponent } from './components/tidy-table/table.component';
import { AiLoaderComponent } from './components/ai-loader/ai-loader.component';
import { AiLoaderCircleComponent } from './components/ai-loader-circle/ai-loader-circle.component';
import { ChannelSyncIndicatorComponent } from './components/channel-sync-indicator/channel-sync-indicator.component';
import { AiImageComponent } from './components/ai-image/ai-image.component';
import { PulseTimelineComponent } from './components/pulse-timeline/pulse-timeline.component';
import { AuthModalComponent } from './components/auth-modal/auth-modal.component';
import { PropertyInfoOverlayComponent } from './components/property-info-overlay/property-info-overlay.component';
import { DropdownMenuComponent } from './components/dropdown-menu/dropdown-menu.component';
import { PopupOverlayComponent } from './components/popup-overlay/popup-overlay.component';
import { AddPropertyFormComponent } from './components/add-property-form/add-property-form.component';
import { GetStartedFlowComponent } from './components/get-started-flow/get-started-flow.component';
import { OnboardingStatusComponent } from './components/onboarding-status/onboarding-status.component';
import { AccountManagerModalComponent } from './components/account-manager-modal/account-manager-modal.component';
import { ActionDockComponent } from './components/action-dock/action-dock.component';
import { GlassCardsComponent } from './components/glass-cards/glass-cards.component';
import { LocationModalComponent } from './components/location-modal/location-modal.component';
import { TaskCardComponent } from './components/task-card/task-card.component';
import { ChatSuggestionCardComponent } from './components/chat-suggestion-card/chat-suggestion-card.component';
import { InquiryCardComponent } from './components/inquiry-card/inquiry-card.component';
import { AutomationsPreviewButtonComponent } from './components/automations-preview-button/automations-preview-button.component';
import { AboutThisPageComponent } from './components/about-this-page/about-this-page.component';

const files = [
  IsSelectedPipe,
  HideAssignedProDidNotContactPipe,
  TaskCardComponent,
  AddInspectionsOnboardingComponent,
  PulseTimelineComponent,
  ServiceRenameOnboardingComponent,
  IntegrateUpsellProviderComponent,
  SelectRestockingOptionOnboardingComponent,
  BillsOnboardingComponent,
  ToDosModalOnboardingComponent,
  TasksOnboardingComponent,
  ScheduleOnboardingComponent,
  UploadProsOnboardingComponent,
  AddProsOnboardingComponent,
  ListingsSetupComponent,
  ConnectBankOnboardingComponent,
  SmartlockTestCodeComponent,
  SmartLockChooserComponent,
  ManageLockAssignmentsComponent,
  LegalComplianceOnboardingComponent,
  GuestMessagingOnboardingComponent,
  DirectBookingOnboardingComponent,
  ListingOptimizerReportComponent,
  ListingOptimizationOnboardingComponent,
  PricingOptimizationOnboardingComponent,
  ChecklistsOnboardingComponent,
  GuidebookOnboardingComponent,
  SmartNumberOnboardingComponent,
  NotifyProsOnboardingComponent,
  OnboardingCallComponent,
  AutomaticBookingFormComponent,
  ConciergeFilterComponent,
  TasksFilterComponent,
  TimeAgoPipe,
  DialogContentComponent,
  OnboardingModalComponent,
  ScheduleWeeklyFilterComponent,
  ScheduleWeeklyViewComponent,
  ScheduleDayViewComponent,
  ScheduleListViewComponent,
  ChipTextArea,
  AccountTypeSelectionComponent,
  TeamTreeComponent,
  AutocompleteAddressComponent,
  BottomNavComponent,
  CameraPreviewComponent,
  DashboardChatComponent,
  DashboardChatSidebarComponent,
  ExpandableChatComponent,
  ChangeSquareClassDirective,
  CleaningTypePlan,
  CustomDatePipe,
  CustomTextareaDirective,
  OutsideClickDirective,
  CustomTimePipe,
  DesktopNavComponent,
  DurationPipe,
  VideoScanner,
  FirstNamePipe,
  ShortNamePipe,
  InitialsPipe,
  TimeIndicatorPipe,
  CapitalizePipe,
  FeedbackComponent,
  SubscriptionModalComponent,
  FormatStatePipe,
  FrequencyFormatPipe,
  TruncatePipe,
  FrequencyRequestTimesComponent,
  GoToNextDirective,
  GenomeItemsComponent,
  ListRequestTimesComponent,
  MessageComponent,
  JobFormPopupComponent,
  DesktopHeaderComponent,
  NotificationHistoryComponent,
  NavBarDesktop,
  ParkingReimbursementComponent,
  ParseDateTimePipe,
  FilterSelectItemsPipe,
  PastHomekeeper,
  PhotoCaption,
  PhotoNote,
  PlanFrequencyMessage,
  RemoveEmployeeComponent,
  RemoveSpacesPipe,
  removeUnderscorePipe,
  ReplaceEnterToBlurDirective,
  RequestTimesSectionComponent,
  SelectOptionsPipe,
  SuccessPage,
  TelephonePipe,
  TidyAbbreviateLastName,
  TidyActiveButtonComponent,
  TidyBigIconComponent,
  TidyCardDirective,
  TidyCardHeader,
  TidyCardIndicator,
  TidyCardInput,
  TidyCardItem,
  TidyCardList,
  TidyCardSelect,
  TidyCardTitleDirective,
  TidyCurrencyPipe,
  TidyLabelDirective,
  TidyLinkDirective,
  TidyMenuAddressSelectorComponent,
  TidyMenuBottomButtonsComponent,
  TidyMenuComponent,
  TidyMenuHeader,
  TidyMenuTopButtonsComponent,
  TidyMissingField,
  TidyNavbar,
  TidyNumberToWeekDayPipe,
  TranslationPipe,
  TidySelectSmall,
  TodosMenu,
  TopNavComponent,
  TranslateOfficePipe,
  TypePlanFormatPipe,
  ToDosOnboardingComponent,
  UrlFileNamePipe,
  MapPopup,
  MaintenancesCardsComponent,
  AddActionModal,
  DropDownMenu,
  DropDownMenuItem,
  TidyDateFormatPipe,
  RecurringTasksFilter,
  FilterNestedFormComponent,
  ScheduleMonthlyViewComponent,
  HeaderComponent,
  PaymentMethodCardComponent,
  RestockingOnboardingComponent,
  RestockingInputsOnboardingComponent,
  PropertyPhotosOnboardingComponent,
  PropertyOnboardingComponent,
  DirectIntegrationOnboardingComponent,
  AddDirectIntegrationOnboardingComponent,
  ImportCSVOnboardingComponent,
  OnboardingComponent,
  GoalTasksCardComponent,
  AdvancedSetupComponent,
  VideoTutorialComponent,
  SelectIntegrationOnboarding,
  EnableAutomaticBookingOnboarding,
  CreateConciergeActionOnboarding,
  SelectAccessNotesOptionOnboarding,
  InputAccessNotesOnboarding,
  SelectFindNewProsOptionOnboarding,
  SelectMaintenanceOptionOnboarding,
  AddMaintenanceItemsOnboarding,
  EnableGuestSmartNumberOnboarding,
  AutomateWorkflowsOnboarding,
  NotifyProsOfChangeOnboarding,
  EnableConciergeMessagingOnboarding,
  CreateConciergeRulesOnboarding,
  AddIntegrationFormComponent,
  PopularListModalOnboardingComponent,
  PdfViewerComponent,
  AccordionComponent,
  MessageCardComponent,
  TidyMarkdownComponent,
  RestockingIntegrationSelectorComponent,
  DynamicBookingFormComponent,
  TaskDropdownComponent,
  StepperComponent,
  ScrollWrapperComponent,
  TableComponent,
  AuthModalComponent,
  PropertyInfoOverlayComponent,
  DropdownMenuComponent,
  PopupOverlayComponent,
  AddPropertyFormComponent,
  GetStartedFlowComponent,
  OnboardingStatusComponent,
  AccountManagerModalComponent,
  ActionDockComponent,
  GlassCardsComponent,
  LocationModalComponent,
  FormatJobDatePipe,
  FormatCentsToDollarsPipe,
  FormatDueDatePipe,
  ServiceDurationPipe,
  IssueHeaderIconPipe,
  WeatherIconPipe,
  ServiceIconPipe,
  ThemeIconPipe,
  MessageTimePipe,
  FormatLocaleNumberPipe,
  AboutThisPageComponent,
  CoverageTypeLabelPipe
];

@NgModule({
  imports: [
    MatTooltipModule,
    CommonModule,
    CdkStepperModule,
    MatStepperModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    FormsModule,
    MatMenuModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatTreeModule,
    MatIconModule,
    IonicModule,
    TagComponent,
    IonicStorageModule.forRoot({
      name: '__mydb',
      driverOrder: [Drivers.IndexedDB, Drivers.LocalStorage]
    }),
    ReactiveFormsModule,
    TidyUiComponentsModule,
    AiLoaderComponent,
    AiLoaderCircleComponent,
    AiImageComponent,
    ChatSuggestionCardComponent,
    InquiryCardComponent,
    AutomationsPreviewButtonComponent,
    CdkScrollable,
    IonicCdkScrollableDirective,
    LinkifyPipe,
    EnrichLinksPipe,
    IsCounterpartyMessagePipe,
    IsTextOnlyGuestChatRoomPipe,
    EveryPipe,
    ChannelSyncIndicatorComponent,
    TidyDateScrollerComponent,
    TidyTimeScrollerComponent,
    IsImageUrlPipe,
    FilenameFromUrlPipe,
    IsPdfUrlPipe,
    IsCsvUrlPipe,
    SafeResourceUrlPipe,
    EscapeHtmlPipe,
    CsvPreviewComponent,
    AttachmentCarouselComponent,
    ImagePreviewComponent,
    DisputeIconPipe,
    ExtractPaymentLinkUuidPipe,
    InlinePaymentFormComponent,
    ReservationCardComponent,
    ReservationPopoverComponent,
    ReservationBarComponent,
    OwnerBlockCardComponent,
    ScheduleWeeklySkeletonComponent,
    RestockingBrandWaterfallComponent,
    BookingRequestsBannerComponent,
  ],
  declarations: files,
  exports: [
    ...files,
    ScheduleWeeklySkeletonComponent,
    BookingRequestsBannerComponent,
    TidyUiComponentsModule,
    TagComponent,
    AiLoaderComponent,
    AiLoaderCircleComponent,
    AiImageComponent,
    ChatSuggestionCardComponent,
    InquiryCardComponent,
    AutomationsPreviewButtonComponent,
    CdkScrollable,
    IonicCdkScrollableDirective,
    LinkifyPipe,
    EnrichLinksPipe,
    IsCounterpartyMessagePipe,
    IsTextOnlyGuestChatRoomPipe,
    EveryPipe,
    ExtractPaymentLinkUuidPipe,
    InlinePaymentFormComponent,
    TidyDateScrollerComponent,
    TidyTimeScrollerComponent,
    IsImageUrlPipe,
    FilenameFromUrlPipe,
    IsPdfUrlPipe,
    IsCsvUrlPipe,
    SafeResourceUrlPipe,
    EscapeHtmlPipe,
    CsvPreviewComponent,
    AttachmentCarouselComponent,
    ImagePreviewComponent,
    DisputeIconPipe,
    RestockingBrandWaterfallComponent,
  ],
  bootstrap: [TeamTreeComponent],
  providers: [
    CustomNavController,
    CustomNavParams,
    FrequencyFormatPipe,
    GetCleaningsService,
    RoutingState,
    TelephonePipe,
    TidyStorage,
    CustomDatePipe,
    TypePlanFormatPipe,
    TranslationPipe,
    DynamicComponentProvider,
    TidyCurrencyPipe,
    TruncatePipe,
    TidyDateFormatPipe,
    JobMediasProvider,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SharedModule { }
